import './add-user.scss'

import clsx from 'clsx'

import { UserInformationBasic } from '@/api'
import {
  GenericValidationMessages,
  Icon,
  IconButton,
  SaveSuccessMessage,
  Space,
  ValidationMessage
} from '@/components/core-v2'
import { UserType } from '@/types/user'

import { useClientsLayout } from '../clients-layout-provider'
import { isErrorWith } from '../common'
import { Mutation } from '../types'
import { RoleDfmAndCatSection } from './role-dfm-and-cat-section'
import { RoleSection } from './role-section'
import { useEditMachine } from './use-edit-machine'

export type Props = {
  errorMessages?: string[]
  errors: { [key: string]: string }
  isSaveSuccess: boolean
  onMutate: (mutation: Mutation<UserInformationBasic>) => void
  onSave: (user: UserInformationBasic) => void
  user?: UserInformationBasic
  userType: UserType
}

export const addUserSuccessMessage = 'User added successfully!'

export function AddUserStateless({
  errorMessages,
  errors,
  isSaveSuccess,
  onMutate,
  onSave,
  user,
  userType
}: Props) {
  const { isPortfolioManagerScreen } = useClientsLayout()

  return (
    <div className="add-new-user">
      <div className="block">
        <label
          className={clsx({
            error: isErrorWith(errors, 'firstName')
          })}
          htmlFor="add-new-user__first-name"
        >
          First name
        </label>
        <input
          className={clsx({
            error: isErrorWith(errors, 'firstName')
          })}
          id="add-new-user__first-name"
          value={user.firstName}
          onChange={v => onMutate(u => (u.firstName = v.target.value))}
        />
        <ValidationMessage errors={errors} fieldName="firstName" />
      </div>
      <div className="block">
        <label
          className={clsx({
            error: isErrorWith(errors, 'lastName')
          })}
          htmlFor="add-new-user__last-name"
        >
          Last name
        </label>
        <input
          className={clsx({
            error: isErrorWith(errors, 'lastName')
          })}
          id="add-new-user__last-name"
          value={user.lastName}
          onChange={v => onMutate(u => (u.lastName = v.target.value))}
        />
        <ValidationMessage errors={errors} fieldName="lastName" />
      </div>
      <div className="block">
        <label
          className={clsx({
            error: isErrorWith(errors, 'email')
          })}
          htmlFor="add-new-user__email"
        >
          Email
        </label>
        <input
          className={clsx({
            error: isErrorWith(errors, 'email')
          })}
          id="add-new-user__email"
          value={user.email}
          onChange={v => onMutate(u => (u.email = v.target.value))}
        />
        <ValidationMessage errors={errors} fieldName="email" />
      </div>
      {userType === 'business-unit' && !isPortfolioManagerScreen && (
        <RoleSection user={user} onMutate={onMutate} />
      )}
      {(userType === 'business-unit' || isPortfolioManagerScreen) && (
        <RoleDfmAndCatSection
          errors={errors}
          isPortfolioManagerScreen={isPortfolioManagerScreen}
          user={user}
          onMutate={onMutate}
        />
      )}
      <GenericValidationMessages errorMessages={errorMessages} />
      <Space isflex mt={3}>
        <IconButton
          data-testid="add-new-user__save-button"
          disabled={false}
          icon={<Icon name="save" size="default" />}
          iconPosition="left"
          operationType="progress"
          text={`Add ${isPortfolioManagerScreen ? 'portfolio manager' : 'new'} user`}
          onClick={() => onSave(user)}
        />
        <Space ml={2}>
          <SaveSuccessMessage isVisible={isSaveSuccess && errorMessages.length === 0}>
            {addUserSuccessMessage}
          </SaveSuccessMessage>
        </Space>
      </Space>
    </div>
  )
}

type AddUserProps = {
  businessUnitId?: string
  clientId?: string
  notifyParentOfSave: () => void
  userType: UserType
}

export function AddUser({ businessUnitId, clientId, notifyParentOfSave, userType }: AddUserProps) {
  const { isPortfolioManagerScreen } = useClientsLayout()

  const { machine, send } = useEditMachine({ notifyParentOfSave })

  return (
    <AddUserStateless
      errorMessages={machine.context.errorMessages}
      errors={machine.context.errors}
      isSaveSuccess={machine.context.isSaveSuccess}
      user={machine.context.user}
      userType={userType}
      onMutate={mutation => send({ type: 'MUTATE', mutation })}
      onSave={user =>
        isPortfolioManagerScreen
          ? send({ type: 'SAVE_NEW_USER', user: user as any, userType, dfmId: clientId })
          : send({ type: 'SAVE_NEW_USER', user: user as any, userType, clientId, businessUnitId })
      }
    />
  )
}
